// Master-P3 Wave 3.2 — Betreiber-Bereich (Operator Tab).
//
// Rendered inside GuidedIntegrationCenter as the "Betreiber-Bereich"
// tab. Content is gated at TWO layers:
//   1. Backend: every endpoint requires role=platform_owner (403 else).
//   2. Frontend: on 403 we render an "Access denied" state so tenant
//      owners get a clear message rather than a broken screen.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
  Alert, AlertDescription, AlertTitle,
} from "../../components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Switch,
} from "../../components/ui/switch";
import {
  ShieldAlert, Wallet, HardDrive, Flag, Lock,
  PlusCircle, Archive, Play, FileWarning,
} from "lucide-react";


function AccessDenied() {
  return (
    <Alert data-testid="operator-access-denied"
           className="max-w-xl">
      <ShieldAlert className="h-4 w-4" />
      <AlertTitle>Betreiber-Zugang erforderlich</AlertTitle>
      <AlertDescription>
        Dieser Bereich ist nur für Nutzer mit der Rolle{" "}
        <code>platform_owner</code> sichtbar. Kein Kunde kann sich
        selbst diese Rolle geben — sie wird ausschließlich per
        Umgebungsvariable{" "}
        <code>SEED_PLATFORM_OWNER=true</code> vergeben.
      </AlertDescription>
    </Alert>
  );
}


// ─── Budget-Frame-Wizard ───────────────────────────────────────────────────

function BudgetFramesSection() {
  const [frames, setFrames] = useState(null);
  const [filterTenant, setFilterTenant] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editFrame, setEditFrame] = useState(null);

  const load = () => {
    const q = filterTenant.trim()
      ? `?tenant_id=${encodeURIComponent(filterTenant.trim())}` : "";
    api.get(`/operator/budget-frames${q}`)
      .then((r) => setFrames(r.data.items || []))
      .catch(() => setFrames([]));
  };

  useEffect(load, [filterTenant]);

  return (
    <div className="space-y-4" data-testid="operator-budget-frames-section">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="tenant-filter">Filter: Tenant-ID</Label>
          <Input id="tenant-filter"
                 value={filterTenant}
                 onChange={(e) => setFilterTenant(e.target.value)}
                 placeholder="leer = alle Tenants"
                 data-testid="input-tenant-filter" />
        </div>
        <Button size="sm" onClick={() => setWizardOpen(true)}
                data-testid="btn-open-budget-wizard">
          <PlusCircle className="h-3 w-3 mr-1" />
          Neuer Budget-Frame
        </Button>
      </div>

      {frames === null && <p className="text-sm">Wird geladen…</p>}
      {frames !== null && frames.length === 0 && (
        <p className="text-sm text-muted-foreground"
           data-testid="budget-frames-empty">
          Keine Budget-Frames vorhanden.
        </p>
      )}
      {frames && frames.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm"
                 data-testid="budget-frames-table">
            <thead className="bg-muted/50 text-xs">
              <tr>
                <th className="text-left p-2">Tenant</th>
                <th className="text-left p-2">Bucket</th>
                <th className="text-right p-2">Limit €</th>
                <th className="text-right p-2">Spent €</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {frames.map((f) => (
                <tr key={f.id} className="border-t"
                    data-testid={`budget-frame-row-${f.id}`}>
                  <td className="p-2 font-mono text-xs">{f.tenant_id?.slice(0, 8)}…</td>
                  <td className="p-2 font-medium">{f.bucket}</td>
                  <td className="p-2 text-right">{Number(f.limit_eur).toFixed(2)}</td>
                  <td className="p-2 text-right text-muted-foreground">
                    {Number(f.spent_eur || 0).toFixed(2)}
                  </td>
                  <td className="p-2">
                    <Badge className={
                      f.status === "active" ? "bg-emerald-100 text-emerald-800 border-0"
                      : f.status === "draft" ? "bg-sky-100 text-sky-800 border-0"
                      : "bg-slate-100 text-slate-600 border-0"}
                      data-testid={`budget-frame-status-${f.id}`}>
                      {f.status}
                    </Badge>
                  </td>
                  <td className="p-2 text-right space-x-1">
                    <Button size="sm" variant="ghost"
                            onClick={() => setEditFrame(f)}
                            data-testid={`btn-edit-frame-${f.id}`}>
                      Bearbeiten
                    </Button>
                    {f.status !== "deprecated" && (
                      <Button size="sm" variant="ghost"
                              onClick={async () => {
                                try {
                                  await api.post(
                                    `/operator/budget-frames/${f.id}/deprecate`);
                                  toast.success("Frame deprecated (soft-delete).");
                                  load();
                                } catch (e) {
                                  toast.error("Deprecate fehlgeschlagen");
                                }
                              }}
                              data-testid={`btn-deprecate-frame-${f.id}`}>
                        <Archive className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wizardOpen && (
        <BudgetFrameWizard open={wizardOpen}
                             onOpenChange={setWizardOpen}
                             onSaved={() => { setWizardOpen(false); load(); }} />
      )}
      {editFrame && (
        <BudgetFrameEditDialog frame={editFrame}
                                 onClose={() => setEditFrame(null)}
                                 onSaved={() => { setEditFrame(null); load(); }} />
      )}
    </div>
  );
}


function BudgetFrameWizard({ open, onOpenChange, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    tenant_id: "", bucket: "llm_text", limit_eur: 25.0, status: "draft",
  });
  const [saving, setSaving] = useState(false);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/operator/budget-frames", form);
      toast.success("Budget-Frame angelegt.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(
        `Anlegen fehlgeschlagen: ${e.response?.data?.detail?.error
                                     || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-frame-wizard">
        <DialogHeader>
          <DialogTitle>Budget-Frame-Wizard — Schritt {step} von 3</DialogTitle>
          <DialogDescription>
            Historisierung: Frames werden nie gelöscht, sondern deprecated.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <div>
              <Label htmlFor="tenant">Tenant-ID</Label>
              <Input id="tenant" value={form.tenant_id}
                     onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                     placeholder="uuid des Tenants"
                     data-testid="wizard-input-tenant-id" />
            </div>
            <div>
              <Label htmlFor="bucket">Bucket</Label>
              <select id="bucket" value={form.bucket}
                      className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                      onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                      data-testid="wizard-select-bucket">
                <option value="llm_text">llm_text</option>
                <option value="image_gen">image_gen</option>
                <option value="video_gen">video_gen</option>
                <option value="crm">crm</option>
                <option value="ads">ads</option>
                <option value="research">research</option>
              </select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <div>
              <Label htmlFor="limit">Limit (EUR)</Label>
              <Input id="limit" type="number" min="0" step="0.5"
                     value={form.limit_eur}
                     onChange={(e) => setForm({
                       ...form, limit_eur: parseFloat(e.target.value) || 0 })}
                     data-testid="wizard-input-limit" />
            </div>
            <div>
              <Label htmlFor="status">Anfangsstatus</Label>
              <select id="status" value={form.status}
                      className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="wizard-select-status">
                <option value="draft">draft</option>
                <option value="active">active (deprecatet vorhandene automatisch)</option>
              </select>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <Alert>
              <FileWarning className="h-4 w-4" />
              <AlertTitle>Bestätigung</AlertTitle>
              <AlertDescription>
                Tenant <code>{form.tenant_id || "(fehlt)"}</code> · Bucket{" "}
                <code>{form.bucket}</code> · Limit €{form.limit_eur} ·
                Status <code>{form.status}</code>.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 1}
                  data-testid="wizard-btn-back">Zurück</Button>
          {step < 3 && (
            <Button onClick={next}
                    disabled={step === 1 && !form.tenant_id.trim()}
                    data-testid="wizard-btn-next">Weiter</Button>
          )}
          {step === 3 && (
            <Button onClick={save} disabled={saving}
                    data-testid="wizard-btn-save">
              {saving ? "Speichere…" : "Frame anlegen"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BudgetFrameEditDialog({ frame, onClose, onSaved }) {
  const [limitEur, setLimitEur] = useState(frame.limit_eur);
  const [status, setStatus] = useState(frame.status);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/operator/budget-frames/${frame.id}`,
        { limit_eur: limitEur, status });
      toast.success("Frame aktualisiert.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(`Update fehlgeschlagen: ${e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="budget-frame-edit-dialog">
        <DialogHeader>
          <DialogTitle>Frame bearbeiten</DialogTitle>
          <DialogDescription>
            {frame.tenant_id?.slice(0, 8)}… / {frame.bucket}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Limit (EUR)</Label>
            <Input type="number" value={limitEur} min="0" step="0.5"
                   onChange={(e) => setLimitEur(parseFloat(e.target.value) || 0)}
                   data-testid="edit-input-limit" />
          </div>
          <div>
            <Label>Status</Label>
            <select value={status}
                    className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="edit-select-status">
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="deprecated">deprecated</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}
                  data-testid="edit-btn-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
                  data-testid="edit-btn-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Storage-Runbook ───────────────────────────────────────────────────────

function StorageRunbookSection() {
  const [cfg, setCfg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [copyResult, setCopyResult] = useState(null);
  const [cutover, setCutover] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/operator/storage/config")
      .then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const run = async (endpoint, setter) => {
    setBusy(true); setter(null);
    try {
      const r = await api.post(`/operator/storage/${endpoint}`,
        { scope: "all" });
      setter(r.data);
    } catch (e) {
      toast.error(`${endpoint} fehlgeschlagen: ${e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="operator-storage-section">
      <Alert data-testid="storage-runbook-safety-banner">
        <Lock className="h-4 w-4" />
        <AlertTitle>Nur Dry-Run über die UI</AlertTitle>
        <AlertDescription>
          Migrations- und Cutover-Kommandos laufen ausschließlich als
          CLI-Aktion (
          <code>python -m app.scripts.storage_migrate --execute</code>).
          Dies ist Absicht: das UI liefert Diagnose, nicht Vollzug.
        </AlertDescription>
      </Alert>

      <Card data-testid="storage-config-card">
        <CardHeader>
          <CardTitle className="text-base">Aktive Storage-Config</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          {!cfg && <p>Wird geladen…</p>}
          {cfg && (
            <>
              <div>Backend: <Badge className="bg-sky-100 text-sky-800 border-0"
                                   data-testid="storage-backend-badge">
                {cfg.backend || "?"}</Badge></div>
              <div>S3-Bucket: <code>{cfg.s3_bucket || "—"}</code></div>
              <div>S3-Region: <code>{cfg.s3_region || "—"}</code></div>
              <div>S3-Credentials: {cfg.s3_credentials_present
                ? <Badge className="bg-emerald-100 text-emerald-800 border-0">
                    vorhanden</Badge>
                : <Badge className="bg-red-100 text-red-800 border-0">
                    fehlen</Badge>}
              </div>
              <div>Local-Root: <code>{cfg.local_assets_root}</code></div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Button variant="outline" disabled={busy}
                onClick={() => run("test-connection", setTestResult)}
                data-testid="btn-storage-test">
          <Play className="h-3 w-3 mr-1" /> Test-Connection
        </Button>
        <Button variant="outline" disabled={busy}
                onClick={() => run("inventory-dry-run", setInventory)}
                data-testid="btn-storage-inventory">
          <Play className="h-3 w-3 mr-1" /> Inventory Dry-Run
        </Button>
        <Button variant="outline" disabled={busy}
                onClick={() => run("copy-dry-run", setCopyResult)}
                data-testid="btn-storage-copy">
          <Play className="h-3 w-3 mr-1" /> Copy Dry-Run
        </Button>
        <Button variant="outline" disabled={busy}
                onClick={() => run("cutover-dry-run", setCutover)}
                data-testid="btn-storage-cutover">
          <Play className="h-3 w-3 mr-1" /> Cutover Dry-Run
        </Button>
      </div>

      {[
        ["storage-test-result", testResult],
        ["storage-inventory-result", inventory],
        ["storage-copy-result", copyResult],
        ["storage-cutover-result", cutover],
      ].map(([tid, data]) => data && (
        <pre key={tid}
             className="text-xs bg-muted/40 rounded p-3 overflow-auto"
             data-testid={tid}>
{JSON.stringify(data, null, 2)}
        </pre>
      ))}
    </div>
  );
}


// ─── Feature-Flag-Editor ───────────────────────────────────────────────────

function FeatureFlagsSection() {
  const [items, setItems] = useState(null);
  const [protectedList, setProtectedList] = useState([]);

  const load = () => {
    api.get("/operator/feature-flags")
      .then((r) => {
        setItems(r.data.items || []);
        setProtectedList(r.data.protected_flags || []);
      })
      .catch(() => setItems([]));
  };

  useEffect(load, []);

  const toggle = async (item) => {
    try {
      await api.put(`/operator/feature-flags/${encodeURIComponent(item.key)}`,
        { enabled: !item.enabled });
      toast.success(`${item.key} → ${!item.enabled}`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "feature_flag_protected") {
        toast.error(
          `${item.key} ist ENV-only und kann nicht per UI geändert werden.`);
      } else {
        toast.error(`Toggle fehlgeschlagen: ${detail?.error || e.message}`);
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="operator-feature-flags-section">
      <Alert data-testid="feature-flags-safety-banner">
        <Lock className="h-4 w-4" />
        <AlertTitle>Geschützte Flags</AlertTitle>
        <AlertDescription>
          Diese Flags sind ENV-only und werden vom Backend hart abgelehnt,
          auch für platform_owner:
          <div className="mt-1 flex flex-wrap gap-1">
            {protectedList.map((k) => (
              <code key={k}
                    className="text-[10px] bg-muted px-1 rounded"
                    data-testid={`protected-flag-${k}`}>{k}</code>
            ))}
          </div>
        </AlertDescription>
      </Alert>

      {items === null && <p className="text-sm">Wird geladen…</p>}
      {items && items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id || item.key}
                 className="flex items-center justify-between border rounded-md p-3"
                 data-testid={`feature-flag-row-${item.key}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{item.key}</span>
                  <Badge className={item.source === "env"
                    ? "bg-amber-100 text-amber-800 border-0"
                    : "bg-slate-100 text-slate-700 border-0"}
                    data-testid={`flag-source-${item.key}`}>
                    {item.source}
                  </Badge>
                  {!item.editable && (
                    <Badge className="bg-red-100 text-red-800 border-0"
                           data-testid={`flag-locked-${item.key}`}>
                      <Lock className="h-3 w-3 mr-1 inline" />read-only
                    </Badge>
                  )}
                </div>
                {item.description && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {item.description}
                  </div>
                )}
              </div>
              <Switch checked={!!item.enabled}
                        disabled={!item.editable}
                        onCheckedChange={() => toggle(item)}
                        data-testid={`flag-toggle-${item.key}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Root ──────────────────────────────────────────────────────────────────

export default function OperatorTab() {
  const [access, setAccess] = useState({ loading: true, ok: false });

  useEffect(() => {
    api.get("/operator")
      .then(() => setAccess({ loading: false, ok: true }))
      .catch((e) => {
        if (e.response?.status === 403) {
          setAccess({ loading: false, ok: false });
        } else {
          setAccess({ loading: false, ok: false });
        }
      });
  }, []);

  if (access.loading) {
    return <p className="text-sm p-4">Prüfe Berechtigung…</p>;
  }
  if (!access.ok) {
    return <div className="p-4"><AccessDenied /></div>;
  }

  return (
    <div className="space-y-6" data-testid="operator-tab">
      <div>
        <h2 className="text-xl font-semibold">Betreiber-Bereich</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
          Cross-Tenant-Werkzeuge: Budget-Frames, Storage-Runbook und
          Feature-Flag-Editor. Alle Aktionen werden im Audit-Log
          festgehalten.
        </p>
      </div>

      <Tabs defaultValue="budget" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="budget"
                       data-testid="op-tab-budget">
            <Wallet className="h-4 w-4 mr-1 inline" />
            Budget-Frames
          </TabsTrigger>
          <TabsTrigger value="storage"
                       data-testid="op-tab-storage">
            <HardDrive className="h-4 w-4 mr-1 inline" />
            Storage-Runbook
          </TabsTrigger>
          <TabsTrigger value="flags"
                       data-testid="op-tab-flags">
            <Flag className="h-4 w-4 mr-1 inline" />
            Feature-Flags
          </TabsTrigger>
        </TabsList>
        <TabsContent value="budget"
                       data-testid="op-panel-budget">
          <BudgetFramesSection />
        </TabsContent>
        <TabsContent value="storage"
                       data-testid="op-panel-storage">
          <StorageRunbookSection />
        </TabsContent>
        <TabsContent value="flags"
                       data-testid="op-panel-flags">
          <FeatureFlagsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
